import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

const STORAGE_KEY = 'meadow-theme';

const MeadowThemeContext = createContext({
  dark: false,
  toggleTheme: () => {},
});

const systemPrefersDark = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
};

/** Hash override (Figma capture) → stored choice → system preference */
const readDarkPref = () => {
  try {
    const hash = window.location.hash || '';
    if (/[#&]mode=dark\b/.test(hash)) return true;
    if (/[#&]mode=light\b/.test(hash)) return false;
    const stored =
      localStorage.getItem(STORAGE_KEY) ??
      sessionStorage.getItem(STORAGE_KEY) ??
      sessionStorage.getItem('home-mural-sleep');
    if (stored === '1' || stored === 'dark') return true;
    if (stored === '0' || stored === 'light') return false;
    return systemPrefersDark();
  } catch {
    return systemPrefersDark();
  }
};

const writeDarkPref = (dark) => {
  try {
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  } catch {
    // Private mode / blocked storage
  }
};

const applyDomTheme = (dark) => {
  const root = document.documentElement;
  root.dataset.theme = dark ? 'dark' : 'light';
  root.style.colorScheme = dark ? 'dark' : 'light';
  // Browser chrome follows the page theme, not only the OS
  const paper = getComputedStyle(root).getPropertyValue('--paper').trim();
  if (paper) document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.setAttribute('content', paper));
};

export const MeadowThemeProvider = ({ children }) => {
  const [dark, setDark] = useState(readDarkPref);

  useLayoutEffect(() => {
    applyDomTheme(dark);
  }, [dark]);

  // Follow the OS only until the user chooses a theme
  useEffect(() => {
    const chosen = () => {
      try {
        return localStorage.getItem(STORAGE_KEY) != null;
      } catch {
        return false;
      }
    };

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => {
      if (!chosen()) setDark(event.matches);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setDark((prev) => {
      const next = !prev;
      writeDarkPref(next);
      return next;
    });
  }, []);

  return (
    <MeadowThemeContext.Provider value={{ dark, toggleTheme }}>
      {children}
    </MeadowThemeContext.Provider>
  );
};

export const useMeadowTheme = () => useContext(MeadowThemeContext);
